// Presence heartbeat (03 provisional / 04 §8.3): `heartbeat(world_id, screen)` every ~20 s while in
// a World — the ready quorum only counts humans with recent presence. The server timestamp of my
// own `world_presence` row as it comes back is the clock sync (11 §8): offset = seen_at − device
// time at send + half the round trip, refreshed on every beat.
import { getConn, onConnected, onReconnected, tsToMs } from './net/connection';
import { rpc } from './net/reducers';
import { useSession } from './stores/session';
import { useRoute } from './stores/route';
import { deviceNow, syncServerTime } from './time';

export const HEARTBEAT_MS = 20_000;

let timer: ReturnType<typeof setInterval> | undefined;
let sentAt = 0;
let started = false;

export function beat(): void {
  const { worldId, identity, phase } = useSession.getState();
  if (!worldId || !identity || !getConn() || (phase !== 'ready' && phase !== 'incompatible')) return;
  sentAt = deviceNow();
  void rpc.heartbeat(worldId, useRoute.getState().screen);
}

export function startPresence(): void {
  if (started) return;
  started = true;
  onConnected((c) => {
    const me = () => useSession.getState().identity;
    const sync = (seenAt: ReturnType<typeof tsToMs>) => {
      if (seenAt === undefined || !sentAt) return;
      const rtt = Math.max(0, deviceNow() - sentAt);
      syncServerTime(seenAt, rtt);
      useSession.getState().touchOffset();
    };
    c.db.worldPresence.onInsert((_ctx, row) => { if (row.owner.toHexString() === me()) sync(tsToMs(row.seenAt)); });
    c.db.worldPresence.onUpdate((_ctx, _old, row) => { if (row.owner.toHexString() === me()) sync(tsToMs(row.seenAt)); });
    beat();
  });
  // docs/54: back from a drop, say so at once (the ready quorum counts recent presence)
  onReconnected(() => beat());
  if (timer) clearInterval(timer);
  timer = setInterval(beat, HEARTBEAT_MS);
  let lastWorld = useSession.getState().worldId, lastPhase = useSession.getState().phase;
  useSession.subscribe((s) => { if (s.worldId !== lastWorld || s.phase !== lastPhase) { lastWorld = s.worldId; lastPhase = s.phase; beat(); } });
  let lastScreen = useRoute.getState().screen;
  useRoute.subscribe((r) => { if (r.screen !== lastScreen) { lastScreen = r.screen; beat(); } });
}
